import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Fade from "@mui/material/Fade";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import CircularProgress from "@mui/material/CircularProgress";
import {
  buildEmptyMapData,
  buildInitialState,
  buildUtilitySliderGroups,
  getUtilitySliderValue,
  isMapFilled,
} from "../../utils/csSkillsState";

const SCORE_MARKS = [
  { value: 0, label: "0" },
  { value: 5, label: "5" },
  { value: 10, label: "10" },
];

const LEVEL_LABELS = {
  0: "None",
  1: "Beginner",
  2: "Beginner",
  3: "Basic",
  4: "Basic",
  5: "Average",
  6: "Good",
  7: "Strong",
  8: "Very Strong",
  9: "Expert",
  10: "Master",
};

function SliderField({ label, value, onChange }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography sx={{ color: "#e0e0e0", fontSize: "0.9rem", fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography sx={{ color: "#E8A33D", fontSize: "0.85rem", fontWeight: 600 }}>
          {value}/10 — {LEVEL_LABELS[value] || ""}
        </Typography>
      </Box>
      <Slider
        value={value}
        onChange={(_, v) => onChange(v)}
        min={0}
        max={10}
        step={1}
        marks={SCORE_MARKS}
        sx={{
          color: "#E8A33D",
          "& .MuiSlider-thumb": { width: 18, height: 18, backgroundColor: "#E8A33D" },
          "& .MuiSlider-track": { height: 6 },
          "& .MuiSlider-rail": { height: 6, backgroundColor: "#41474E" },
          "& .MuiSlider-markLabel": { color: "#888", fontSize: "0.7rem" },
        }}
      />
    </Box>
  );
}

SliderField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function TacticalProfilePage({ actions, catalog }) {
  const { user, csSkills, csSkillsTemplate } = catalog || {};
  const [activeTab, setActiveTab] = useState(0);
  const [localSkills, setLocalSkills] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    actions.getCsSkillsTemplate();
    if (user?.userId) {
      actions.getCsSkills({ userId: user.userId });
    }
  }, [user?.userId]);

  useEffect(() => {
    if (csSkillsTemplate) {
      setLocalSkills(buildInitialState(csSkillsTemplate, csSkills));
    }
  }, [csSkillsTemplate, csSkills]);

  const template = csSkillsTemplate;
  const maps = template?.maps || [];
  const currentMapKey = maps[activeTab]?.key;
  const utilitySliderGroups = useMemo(
    () => buildUtilitySliderGroups(template?.utilities),
    [template?.utilities],
  );

  const filledCount = useMemo(() => {
    return maps.filter((m) => isMapFilled(localSkills[m.key])).length;
  }, [localSkills, maps]);

  const handleFieldChange = useCallback((section, key, value) => {
    setLocalSkills((prev) => {
      const mapData = prev[currentMapKey] || buildEmptyMapData(template);
      if (section === "stratKnowledge") {
        return { ...prev, [currentMapKey]: { ...mapData, stratKnowledge: value } };
      }
      return {
        ...prev,
        [currentMapKey]: {
          ...mapData,
          [section]: { ...mapData[section], [key]: value },
        },
      };
    });
    setSaved(false);
  }, [currentMapKey, template]);

  const handleUtilityGroupChange = useCallback((keys, value) => {
    setLocalSkills((prev) => {
      const mapData = prev[currentMapKey] || buildEmptyMapData(template);
      const nextUtilities = { ...mapData.utilities };
      keys.forEach((key) => {
        nextUtilities[key] = value;
      });

      return {
        ...prev,
        [currentMapKey]: {
          ...mapData,
          utilities: nextUtilities,
        },
      };
    });
    setSaved(false);
  }, [currentMapKey, template]);

  const handleSave = async () => {
    if (!user?.userId) return;
    setSaving(true);
    setSaved(false);
    await actions.saveCsSkills({ userId: user.userId, csSkills: { maps: localSkills } });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!template) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: "#E8A33D" }} />
      </Box>
    );
  }

  const mapData = localSkills[currentMapKey] || buildEmptyMapData(template);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: 2, py: 4 }}>
      <Typography sx={{ fontSize: "1.8rem", fontWeight: 700, color: "#fff", mb: 0.5 }}>
        Tactical Profile
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", color: "#b5b1b3", mb: 3, lineHeight: 1.5 }}>
        Rate your CS2 skills per map to help us create balanced, competitive matches.
        Your tactical profile is only visible to your teammates during a match.
      </Typography>

      <Paper sx={{ backgroundColor: "#1C1E20", borderRadius: 3, border: "1px solid #333", overflow: "hidden" }}>
        <Box sx={{ borderBottom: "1px solid #333" }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                color: "#888",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.9rem",
                minHeight: 56,
                transition: "color 0.15s ease-in-out",
                "&.Mui-selected": { color: "#E8A33D" },
              },
              "& .MuiTabs-indicator": { backgroundColor: "#E8A33D", height: 3 },
            }}
          >
            {maps.map((m, idx) => {
              const filled = isMapFilled(localSkills[m.key]);
              return (
                <Tab
                  key={m.key}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      {m.display}
                      {filled && <CheckCircleIcon sx={{ fontSize: "1rem", color: "#38B000" }} />}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        <Fade in timeout={300} key={currentMapKey}>
          <Box sx={{ p: 3 }}>
            {/* Utility Knowledge */}
            <Typography sx={{ color: "#E8A33D", fontSize: "1.05rem", fontWeight: 600, mb: 2 }}>
              Utility Knowledge
            </Typography>
            {utilitySliderGroups.map((group) => (
              <SliderField
                key={group.keys.join("-")}
                label={group.label}
                value={getUtilitySliderValue(mapData, group.keys)}
                onChange={(v) => handleUtilityGroupChange(group.keys, v)}
              />
            ))}

            {/* Strat Knowledge */}
            <Typography sx={{ color: "#E8A33D", fontSize: "1.05rem", fontWeight: 600, mt: 3, mb: 2 }}>
              Strat Knowledge
            </Typography>
            <SliderField
              label="Strategy & Executes"
              value={mapData.stratKnowledge || 0}
              onChange={(v) => handleFieldChange("stratKnowledge", null, v)}
            />

            {/* Role Proficiency */}
            <Typography sx={{ color: "#E8A33D", fontSize: "1.05rem", fontWeight: 600, mt: 3, mb: 2 }}>
              Role Proficiency
            </Typography>
            {(template.roles || []).map((r) => (
              <SliderField
                key={r.key}
                label={r.display}
                value={mapData.roles?.[r.key] || 0}
                onChange={(v) => handleFieldChange("roles", r.key, v)}
              />
            ))}
          </Box>
        </Fade>
      </Paper>

      {/* Progress + Save */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
        <Typography sx={{ color: "#b5b1b3", fontSize: "0.9rem" }}>
          {filledCount}/{maps.length} maps filled
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
          sx={{
            backgroundColor: "#E8A33D",
            fontWeight: 600,
            fontSize: "0.95rem",
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: 2,
            "&:hover": { backgroundColor: "#E55A00" },
            "&.Mui-disabled": { backgroundColor: "#666", color: "#aaa" },
          }}
        >
          {saving ? "Saving..." : saved ? "Saved!" : "Save All Maps"}
        </Button>
      </Box>
    </Box>
  );
}

TacticalProfilePage.propTypes = {
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};
